import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";

/**
 * OwnerBreadcrumbs — trail beneath the top bar.
 *
 * Taxonomy is 1:1 with the OwnerSidebar groups (single navigation language):
 *   Insight · Revenue · Customers · Trust & Compliance · Infrastructure · Settings
 *
 * Example:
 *     Platform Owner  ›  Trust & Compliance  ›  Security Events
 */
const MAP = {
  "/owner":                 { group: null,                   label: "Overview" },

  // ── Insight ─────────────────────────────────────────────────────────
  "/owner/attention":       { group: "Insight",              label: "Attention Center" },
  "/owner/growth":          { group: "Insight",              label: "Growth Analytics" },
  "/owner/digest":          { group: "Insight",              label: "Weekly Digest" },

  // ── Revenue ─────────────────────────────────────────────────────────
  "/owner/billing/refunds": { group: "Revenue",              label: "Refunds & Disputes" },
  "/owner/billing/config":  { group: "Revenue",              label: "Billing Configuration" },
  "/owner/billing":         { group: "Revenue",              label: "Subscriptions" },

  // ── Customers ───────────────────────────────────────────────────────
  "/owner/orgs":            { group: "Customers",            label: "Organizations" },

  // ── Trust & Compliance ──────────────────────────────────────────────
  "/owner/identity":        { group: "Trust & Compliance",   label: "Identity Verification" },
  "/owner/security":        { group: "Trust & Compliance",   label: "Security Events" },
  "/owner/soc":             { group: "Trust & Compliance",   label: "SOC Actions" },
  "/owner/audit":           { group: "Trust & Compliance",   label: "Audit Log" },
  "/owner/privileged-emails": { group: "Trust & Compliance", label: "Privileged Emails" },

  // ── Infrastructure ──────────────────────────────────────────────────
  "/owner/production":      { group: "Infrastructure",       label: "Environment" },
  "/owner/providers":       { group: "Infrastructure",       label: "Providers" },
  "/owner/storage":         { group: "Infrastructure",       label: "Storage" },
  "/owner/launch":          { group: "Infrastructure",       label: "Launch Wizard" },
  "/owner/readiness":       { group: "Infrastructure",       label: "Readiness" },
  "/owner/staff":           { group: "Infrastructure",       label: "Platform Staff" },

  // ── Settings ────────────────────────────────────────────────────────
  "/owner/notifications":   { group: "Settings",             label: "Notifications" },
  "/owner/settings":        { group: "Settings",             label: "Settings" },

  // ── Developer (read-only design workspace) ──────────────────────────
  "/owner/design":          { group: "Developer",            label: "Design Workspace" },
};

export default function OwnerBreadcrumbs() {
  const loc = useLocation();
  // Match longest prefix so /owner/orgs/:id inherits from /owner/orgs.
  const match = Object.keys(MAP)
    .filter((k) => loc.pathname === k || loc.pathname.startsWith(k + "/"))
    .sort((a, b) => b.length - a.length)[0];
  const entry = MAP[match || "/owner"] || { group: null, label: "Overview" };

  return (
    <nav className="hbf-breadcrumbs" aria-label="Breadcrumb" data-testid="owner-breadcrumbs">
      <Link to="/owner" data-testid="crumb-home">Platform Owner</Link>
      {entry.group && (
        <>
          <ChevronRight size={12} strokeWidth={2} className="hbf-breadcrumbs__sep" />
          <span data-testid="crumb-group">{entry.group}</span>
        </>
      )}
      <ChevronRight size={12} strokeWidth={2} className="hbf-breadcrumbs__sep" />
      <span className="hbf-breadcrumbs__current" data-testid="crumb-current">{entry.label}</span>
    </nav>
  );
}
